import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { Icon } from '@/components/common/Icon';
import { AdminPageHeader } from '@/components/admin/shared/AdminPageHeader';
import { PatientOptionsSection } from './PatientOptionsSection';
import { ConsultationSettingsSection } from './ConsultationSettingsSection';
import { PlanTemplatesSection } from './PlanTemplatesSection';
import { ConsentDocumentsSection } from './ConsentDocumentsSection';
import { IntegrationsSection } from './IntegrationsSection';
import { MyAccountSection } from './MyAccountSection';
import { BrandingSection } from '@/components/practitioner/BrandingPage/BrandingSection';
import { TreatmentLibrarySection } from '@/components/practitioner/TreatmentLibrary';
import { ServicesSection } from '@/components/practitioner/Services';
import { NotificationPreferencesSection } from '@/components/notifications/NotificationPreferencesSection';
import { useAuth } from '@/hooks/useAuth';
import { useCanCustomizeAssistant } from '@/hooks/useCanCustomizeAssistant';
import { AssistantSettingsSection } from './AssistantSettingsSection';
import { SETTINGS_TAB_SEARCH_PARAMETER } from '@/components/assistant/uiActions/assistantDestinations';
import {
  WORKSPACE_SETTINGS_TABS,
  type WorkspaceSettingsTabDefinition,
  type WorkspaceSettingsTabId,
} from './tabs';

export function WorkspaceSettingsPage() {
  const { t } = useTranslation('settings');
  const { enabledModules, activeWorkspace, isElevatedWorkspaceAccess } = useAuth();
  const isWorkspaceOwner =
    (activeWorkspace !== null && activeWorkspace.isOwner === true) || isElevatedWorkspaceAccess;
  const canCustomizeAssistant = useCanCustomizeAssistant(isWorkspaceOwner);
  const [searchParameters, setSearchParameters] = useSearchParams();
  const requestedTabId = searchParameters.get(SETTINGS_TAB_SEARCH_PARAMETER);
  function setActiveTabId(nextTabId: WorkspaceSettingsTabId) {
    setSearchParameters({ [SETTINGS_TAB_SEARCH_PARAMETER]: nextTabId }, { replace: true });
  }
  const visibleTabs = WORKSPACE_SETTINGS_TABS.filter(function isVisible(tab) {
    if (tab.visibleWhen !== undefined) {
      return tab.visibleWhen({
        enabledModules,
        isOwner: isWorkspaceOwner,
        canCustomizeAssistant,
      });
    }
    const moduleAllowed =
      tab.requiredModule === undefined || enabledModules.includes(tab.requiredModule);
    const ownerAllowed = tab.ownerOnly !== true || isWorkspaceOwner;
    return moduleAllowed && ownerAllowed;
  });
  const activeTab = visibleTabs.find(function isActive(tab) {
    return tab.id === requestedTabId;
  });
  const resolvedActiveTab = activeTab === undefined ? visibleTabs[0] : activeTab;

  return (
    <div className="p-4 sm:p-6 md:p-8 lg:h-full lg:flex lg:flex-col">
      <AdminPageHeader title={t('practitioner.title')} subtitle={t('practitioner.subtitle')} />

      <div className="flex flex-col lg:flex-row gap-5 lg:gap-8 lg:flex-1 lg:min-h-0">
        <div className="relative w-full lg:contents">
          <nav className="w-full lg:w-60 flex-shrink-0 flex flex-row lg:flex-col gap-2 overflow-x-auto no-scrollbar lg:overflow-visible">
            {visibleTabs.map(function renderTabButton(tab) {
              return (
                <SettingsTabButton
                  key={tab.id}
                  tab={tab}
                  isActive={resolvedActiveTab !== undefined && tab.id === resolvedActiveTab.id}
                  onSelect={function select() {
                    setActiveTabId(tab.id);
                  }}
                />
              );
            })}
          </nav>
          <div className="pointer-events-none absolute top-0 bottom-0 right-0 w-10 bg-gradient-to-l from-zoooffwhite to-transparent lg:hidden" />
        </div>

        <div className="flex-1 min-w-0 lg:min-h-0 lg:overflow-y-auto lg:pr-1">
          {resolvedActiveTab !== undefined && resolvedActiveTab.id === 'patientOptions' ? (
            <PatientOptionsSection />
          ) : resolvedActiveTab !== undefined && resolvedActiveTab.id === 'treatmentLibrary' ? (
            <TreatmentLibrarySection />
          ) : resolvedActiveTab !== undefined && resolvedActiveTab.id === 'services' ? (
            <ServicesSection />
          ) : resolvedActiveTab !== undefined && resolvedActiveTab.id === 'consultations' ? (
            <ConsultationSettingsSection />
          ) : resolvedActiveTab !== undefined && resolvedActiveTab.id === 'planTemplates' ? (
            <PlanTemplatesSection />
          ) : resolvedActiveTab !== undefined && resolvedActiveTab.id === 'consentDocuments' ? (
            <ConsentDocumentsSection />
          ) : resolvedActiveTab !== undefined && resolvedActiveTab.id === 'branding' ? (
            <BrandingSection />
          ) : resolvedActiveTab !== undefined && resolvedActiveTab.id === 'integrations' ? (
            <IntegrationsSection />
          ) : resolvedActiveTab !== undefined &&
            resolvedActiveTab.id === 'notificationPreferences' ? (
            <NotificationPreferencesSection />
          ) : resolvedActiveTab !== undefined && resolvedActiveTab.id === 'assistant' ? (
            <AssistantSettingsSection />
          ) : resolvedActiveTab !== undefined && resolvedActiveTab.id === 'myAccount' ? (
            <MyAccountSection />
          ) : null}
        </div>
      </div>
    </div>
  );
}

interface SettingsTabButtonProps {
  tab: WorkspaceSettingsTabDefinition;
  isActive: boolean;
  onSelect: () => void;
}

function SettingsTabButton({ tab, isActive, onSelect }: SettingsTabButtonProps) {
  const { t } = useTranslation('settings');

  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        'flex-shrink-0 w-auto lg:w-full flex items-center gap-2.5 px-4 py-2.5 lg:py-3 rounded-xl text-sm font-medium transition-all whitespace-nowrap',
        isActive
          ? 'bg-zooprimary text-zoowhite shadow-sm'
          : 'bg-zoowhite border border-zooborder text-zoomuted hover:border-zooprimary hover:text-zooprimary'
      )}
    >
      <Icon name={tab.iconName} className="text-base flex-shrink-0" />
      <span className="flex-1 text-left">{t(tab.labelKey)}</span>
    </button>
  );
}
